import type { Breadcrumb, ErrorEvent } from "@sentry/nextjs";
import { getEnvironment } from "@/helpers/environment";

/**
 * What every Sentry runtime here shares: browser, Node and edge.
 *
 * The SDK's defaults collect generously, and this site handles supporter
 * details and payment flows, so the defaults are switched off rather than
 * trusted. What is left is the error, its stack and where it happened.
 */

/**
 * Breadcrumbs that record a URL or a log line.
 *
 * - `fetch` / `xhr` / `http`: cart requests carry the cart ID in the path, and
 *   the cart ID is the only credential Medusa asks for (CyberCX finding 3.1.5).
 *   Gateway requests carry search terms, which can be email addresses.
 * - `console`: the gateway client logs full request URLs.
 *
 * Clicks and navigation are kept, which is most of what makes a browser error
 * reproducible.
 */
const DROPPED_BREADCRUMBS = new Set(["console", "fetch", "xhr", "http"]);

const beforeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null =>
  breadcrumb.category && DROPPED_BREADCRUMBS.has(breadcrumb.category) ? null : breadcrumb;

/**
 * Errors that are never ours to fix, dropped before they are sent.
 *
 * - "Object Not Found Matching Id": Microsoft's email link scanner (Outlook
 *   Safe Links) opening a page and rejecting a promise with a plain object.
 *   No person ever sees it.
 * - A failed request to a tracking host: an ad blocker or privacy extension
 *   refusing it. The host is in the message because the SDK appends it when
 *   it builds the event, which is before this list is checked.
 *   `sgtm.baptistworldaid.org.au` is our own server-side Tag Manager, and
 *   blockers refuse it by its `sgtm.` name.
 * - `window.webkit.messageHandlers.navigationPerformanceLoggerWithReply`: a
 *   script the Instagram and Facebook in-app browser on iOS injects into every
 *   page it opens. Its call to the native handler throws and the rejection is
 *   unhandled; the handler name is the app's, not ours. Nothing
 *   in this repository references it. It arrives on paid-social landing pages
 *   (BAPTIST-WORLD-AID-1F). Only that handler is matched, so a real use of
 *   `window.webkit` in our own code would still be reported.
 *
 * A failed request to anything else is kept: Medusa, the gateway and our own
 * routes fail the same way, and those failures are the ones that matter.
 */
export const IGNORED_ERRORS = [
  /Object Not Found Matching Id:\d+, MethodName:/,
  /(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.) \((?:sgtm\.baptistworldaid\.org\.au|ad\.doubleclick\.net|reader\.self)\)/,
  /window\.webkit\.messageHandlers\.navigationPerformanceLoggerWithReply\.postMessage\b/,
];

/**
 * Whether an event came from the embedded Sanity Studio, served under `/studio`.
 *
 * The Studio is the editors' tool, not the supporter's site, and its own
 * failures (the visual editor's timeouts, for one) are noise next to the pages
 * supporters use. The page the error happened on is what decides: the SDK
 * records it as `request.url` in the browser, and the route as `transaction`
 * on the server. Only `/studio` itself and paths beneath it match, so
 * `/studio-appeal` and every public route are untouched.
 */
const STUDIO_PATH = /^\/studio(?:\/|$)/;

const pathOfUrl = (url: string | undefined): string => {
  if (!url) {
    return "";
  }
  try {
    return new URL(url, "https://placeholder.invalid").pathname;
  } catch {
    return "";
  }
};

export const isStudioEvent = (event: Pick<ErrorEvent, "request" | "transaction">): boolean =>
  STUDIO_PATH.test(pathOfUrl(event.request?.url)) || STUDIO_PATH.test(event.transaction ?? "");

const beforeSend = (event: ErrorEvent): ErrorEvent | null => (isStudioEvent(event) ? null : event);

export const sentryOptions = {
  // Unset means the SDK initialises and sends nothing, so local development
  // and any deploy without the variable are unaffected.
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: getEnvironment(),
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: false,
    httpBodies: [],
    urlQueryParams: false,
  },
  // The default rewrites the message on the error object itself, appending the
  // hostname. Several call sites match on the exact text of a failed fetch
  // (`tools/portal/errors.ts` among them), so the rewrite is confined to what
  // Sentry is sent.
  enhanceFetchErrorMessages: "report-only" as const,
  beforeBreadcrumb,
  beforeSend,
  ignoreErrors: IGNORED_ERRORS,
};

/**
 * The share of server requests to trace, from `SENTRY_TRACES_SAMPLE_RATE`.
 *
 * Read from the environment so tracing is switched on per Netlify deploy
 * context without a code change: set on deploy previews first, left unset
 * everywhere else. Unset, empty, zero or nonsense all mean off, which is
 * `undefined` to the SDK. Deliberately not `NEXT_PUBLIC_`: only the server
 * traces, and the value is read at runtime instead of being baked into a build.
 */
export const tracesSampleRate = (
  raw: string | undefined = process.env.SENTRY_TRACES_SAMPLE_RATE,
): number | undefined => {
  const rate = Number(raw);
  if (!raw || !Number.isFinite(rate) || rate <= 0) {
    return undefined;
  }
  return Math.min(rate, 1);
};

/** What the SDK tells a sampler about the request it is deciding on. */
interface SamplingRequest {
  name: string;
  attributes?: Record<string, unknown>;
  normalizedRequest?: { url?: string };
}

/** The path of a URL or a bare path, without the query. Empty if unreadable. */
const pathOf = (value: unknown): string => {
  if (typeof value !== "string" || !value) {
    return "";
  }
  try {
    return new URL(value, "https://placeholder.invalid").pathname;
  } catch {
    return "";
  }
};

/**
 * Whether a request is for one of our own API routes.
 *
 * The SDK describes an incoming request in more than one place depending on
 * the runtime, so all of them are checked: the request it normalised, the
 * `http.target` attribute, and the span name, which reads `GET /api/session`.
 */
export const isApiRouteRequest = ({
  name,
  attributes,
  normalizedRequest,
}: SamplingRequest): boolean =>
  [
    pathOf(normalizedRequest?.url),
    pathOf(attributes?.["http.target"]),
    pathOf(name.split(" ")[1]),
  ].some((path) => path.startsWith("/api/"));

/**
 * Trace our API routes, at the configured rate, and nothing else.
 *
 * Left to itself the SDK traces every page render, every middleware run and
 * the platform's own traffic, and the calls worth seeing are a small part of
 * that. The API routes are where the browser reaches the API Gateway through
 * us, so those are the requests kept.
 *
 * Returns `undefined` when no rate is set, so tracing stays fully off instead
 * of running a sampler that always answers zero.
 *
 * The rate is a required argument, not a default read from the environment:
 * the lint autofix strips an explicit `undefined` from a call, which would
 * silently turn "no rate" in a test into "whatever the environment says".
 */
export const tracesSampler = (rate: number | undefined) =>
  rate === undefined
    ? undefined
    : (request: SamplingRequest): number => (isApiRouteRequest(request) ? rate : 0);

const escapeRegExp = (value: string): string =>
  value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);

/**
 * The spans to leave out of a traced API route.
 *
 * What remains is the route itself and its calls to the API Gateway.
 *
 * - Netlify's plumbing: its Next.js runtime reads the page cache from Netlify
 *   Blobs a dozen or more times per request, and fetches through its own edge.
 * - Next.js bookkeeping spans, which last a fraction of a millisecond.
 * - Any outgoing request that is not to the gateway: Sanity, Medusa, Auth0,
 *   reCAPTCHA. Matched as "an outgoing request whose description does not
 *   contain the gateway's host", since there is no way to list the hosts to
 *   keep. Skipped when the gateway URL is unknown, so nothing is hidden by
 *   accident.
 */
export const ignoredSpans = (gatewayUrl: string | undefined) => {
  let gatewayHost = "";
  try {
    gatewayHost = gatewayUrl ? new URL(gatewayUrl).host : "";
  } catch {
    gatewayHost = "";
  }

  return [
    "netlifyblobs.com",
    "netliedge.com",
    "resolve page components",
    "start response",
    ...(gatewayHost
      ? [{ op: "http.client", name: new RegExp(`^(?!.*${escapeRegExp(gatewayHost)})`) }]
      : []),
  ];
};
